import apiClient from "./api-client";
import { handleApiError } from "./error-handler";
import {
  Client,
  ClientCreateRequest,
  ClientUpdateRequest
} from "../models/Client";
import { PaginatedResponse } from "../models/Pagination";
import { ClientSearchResult } from "../models/SignatoryClient";

/**
 * Get the current user's client
 */
export const getCurrentClient = async (): Promise<Client | null> => {
  try {
    const response = await apiClient.get('/clients/me');
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return null;
    }
    throw handleApiError(error, "Failed to get client");
  }
};

/**
 * Create a new client
 */
export const createClient = async (client: ClientCreateRequest): Promise<Client> => {
  try {
    const response = await apiClient.post('/clients', client);
    return response.data;
  } catch (error) {
    throw handleApiError(error, "Failed to create client");
  }
};

/**
 * Update a client
 */
export const updateClient = async (clientData: ClientUpdateRequest, userId?: number): Promise<void> => {
  try {
    const endpoint = userId ? `/clients/${userId}` : '/clients';
    await apiClient.put(endpoint, clientData);
  } catch (error) {
    throw handleApiError(error, "Failed to update client");
  }
};

/**
 * Get paginated list of clients - Only accessible to Admin and CSM
 * @param page Page number (1-based)
 * @param pageSize Items per page
 */
export const getClients = async (
  page = 1,
  pageSize = 10,
  sortField?: string,
  sortDirection?: string
): Promise<PaginatedResponse<Client>> => {
  try {
    let url = `/clients?page=${page}&pageSize=${pageSize}`;
    if (sortField) {
      url += `&sortField=${encodeURIComponent(sortField)}`;
    }
    if (sortDirection) {
      url += `&sortDirection=${encodeURIComponent(sortDirection)}`;
    }

    const response = await apiClient.get<PaginatedResponse<Client>>(url);

    // The API now returns the paginated response directly in the body
    return response.data;
  } catch (error) {
    throw handleApiError(error, "Failed to fetch clients");
  }
};

/**
 * Get client by user ID - For individual client details
 * @param userId Client's user ID
 */
export const getClientByUserId = async (userId: number): Promise<Client> => {
  try {
    const response = await apiClient.get(`/clients/${userId}`);
    return response.data;
  } catch (error) {
    throw handleApiError(error, "Failed to fetch client");
  }
};

/**
 * Search clients with pagination - Only accessible to Admin and CSM
 * @param searchTerm Search term to match against name, email, company, or job title
 * @param page Page number (1-based)
 * @param pageSize Items per page
 * @param onboardingCompleted Optional filter for onboarding status
 * @param companyId Optional filter for company
 */
export const searchClients = async (
  searchTerm: string,
  page = 1,
  pageSize = 10,
  onboardingCompleted?: boolean,
  companyId?: number,
  sortField?: string,
  sortDirection?: string,
  hasMatchings?: boolean
): Promise<PaginatedResponse<Client>> => {
  try {
    let url = `/clients/search?searchTerm=${encodeURIComponent(searchTerm.trim())}&page=${page}&pageSize=${pageSize}`;

    if (onboardingCompleted !== undefined) {
      url += `&onboardingCompleted=${onboardingCompleted}`;
    }
    if (companyId !== undefined) {
      url += `&companyId=${companyId}`;
    }
    if (hasMatchings !== undefined) {
      url += `&hasMatchings=${hasMatchings}`;
    }
    if (sortField) {
      url += `&sortField=${encodeURIComponent(sortField)}`;
    }
    if (sortDirection) {
      url += `&sortDirection=${encodeURIComponent(sortDirection)}`;
    }

    const response = await apiClient.get<PaginatedResponse<Client>>(url);
    return response.data;
  } catch (error) {
    throw handleApiError(error, "Failed to search clients");
  }
};

/**
 * Search for a client by email
 * Returns existing platform user info if found
 */
export const searchClientByEmail = async (email: string): Promise<ClientSearchResult> => {
  try {
    const response = await apiClient.get<ClientSearchResult>('/clients/search-by-email', {
      params: { email }
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error, "Failed to search client by email");
  }
};